import { useI18n } from '@/hooks/useI18n';
import { Activity } from 'lucide-react';
import { PathLabel } from '@/components/primitives/PathLabel';
import { ErrorInline } from '@/components/primitives/ErrorInline';
import { Stagger } from '@/components/primitives/Stagger';
import {
  useAccountsHealth,
  useSchedulerRuns,
  useSchedulerStatus,
} from '@/hooks/queries/useScheduler';
import { useAuth } from '@/contexts/AuthContext';
import { formatNumber, formatRelative } from '@/lib/format';
import { normalizeQuotaRemaining } from '@/lib/scheduler';
import { RAPIDAPI_QUOTA_MAX } from '@/lib/constants';
import { cn } from '@/lib/cn';
import { RunsStrip } from './RunsStrip';
import { RunsFeed } from './RunsFeed';
import { TriggersPanel } from './TriggersPanel';
import { HealthList } from './HealthList';

export function OpsScreen() {
  const { t } = useI18n();
  const status = useSchedulerStatus();
  const runs = useSchedulerRuns({ size: 20 });
  const health = useAccountsHealth();
  const { user } = useAuth();

  const quota = normalizeQuotaRemaining(status.data?.quotaRemaining);
  // Inclut volontairement quota === 0 dans le warn (quota épuisé = état le plus critique).
  // Seul le cas "indisponible" (quota === null) court-circuite l'alerte rouge.
  const warn = quota !== null && quota / RAPIDAPI_QUOTA_MAX < 0.25;
  const observedAt = quota !== null ? (status.data?.quotaObservedAt ?? null) : null;

  return (
    <Stagger className="space-y-5">
      <header className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className="min-w-0">
          <PathLabel
            path="/ops"
            meta={
              <span className="flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald" />
                live · 30s
              </span>
            }
          />
          <h1 className="mt-1 flex items-center gap-2 text-[22px] font-semibold tracking-tight text-text-1">
            <Activity className="h-5 w-5 text-violet-deep" />
            {t('Exploitation', 'Operations')}
          </h1>
          <p className="text-[13px] text-text-3">
            {t(
              'Scheduler, runs et état des comptes en temps réel.',
              'Scheduler, runs and account status in real time.',
            )}
          </p>
        </div>
        <div
          className={cn(
            'flex max-w-full flex-wrap items-center gap-x-2 gap-y-1 self-start rounded-2xl border px-3 py-2 text-[12px] tabular-nums shadow-card sm:rounded-full sm:py-1.5',
            warn ? 'border-rose/30 bg-rose/10 text-rose' : 'border-line-1 bg-bg-1 text-text-2',
          )}
        >
          <span className="text-text-3">Quota</span>
          <span className="font-semibold text-text-1">{formatNumber(quota)}</span>
          <span className="text-text-4">/</span>
          <span className="text-text-4">{formatNumber(RAPIDAPI_QUOTA_MAX)}</span>
          {observedAt && <span className="ml-1 text-text-4">· {formatRelative(observedAt)}</span>}
        </div>
      </header>

      {status.error && <ErrorInline error={status.error} onRetry={() => status.refetch()} />}

      {!status.error && <RunsStrip data={status.data} loading={status.isLoading} />}

      {!status.error && (
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_300px]">
          <div className="min-w-0 space-y-4">
            <RunsFeed runs={runs.data?.content} loading={runs.isLoading} />
            <HealthList accounts={health.data} loading={health.isLoading} />
          </div>
          {user?.role === 'ADMIN' && <TriggersPanel />}
        </div>
      )}
    </Stagger>
  );
}
